import { useState, useRef, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import API from '@Services/apiAxios';
import axios from 'axios';

// ==================== TYPES ====================
export interface BotMessage {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp: Date;
  state?: 'complete' | 'awaiting_input' | 'awaiting_confirmation' | 'prefill_ui';
  uiAction?: {
    editUrl: string;
    sections?: {
      consignee?: any;
      shipper?: any;
      package?: any;
    };
  };
  relatedChips?: Array<{
    id: string;
    label: string;
    query: string;
  }>;
  attachmentPreviews?: Array<{ name: string; size: string; type: string }>;
  s3Key?: string;
}

export interface ApiResponse {
  message: string;
  state: 'complete' | 'awaiting_input' | 'awaiting_confirmation' | 'prefill_ui';
  uiAction?: {
    editUrl: string;
    sections?: any;
  };
  relatedChips?: Array<{
    id: string;
    label: string;
    query: string;
  }>;
}

export interface useChatbotStateProps {
  orgCode: string;
  onNavigate?: (url: string) => void;
  apiBaseUrl?: string;
}

export const useChatbotState = ({ orgCode, onNavigate, apiBaseUrl }: useChatbotStateProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<BotMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFileDialog, setShowFileDialog] = useState(false);

  // Gemini animation state
  const [isActivating, setIsActivating] = useState(false);

  const userName = useSelector(
    (state: RootStateType) =>
      state.loginUser.profile?.name || state.loginUser.profile?.email || state.loginUser.profile?.username || 'User',
  );

  // Bot-specific state
  const [sessionId, setSessionId] = useState('');
  const [currentBotState, setCurrentBotState] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<BotMessage[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    if (!sessionId && isOpen) {
      const savedSessionId = sessionStorage.getItem('botSessionId') || uuidv4();
      sessionStorage.setItem('botSessionId', savedSessionId);
      setSessionId(savedSessionId);

      console.group('🤖 BOT INITIALIZATION');
      console.info('📌 Session ID Generated:', savedSessionId);
      console.info('🏢 Organization Code:', orgCode);
      console.info('⏰ Timestamp:', new Date().toISOString());
      console.info('🔗 API Base URL:', apiBaseUrl);
      console.groupEnd();
    }
  }, [isOpen, orgCode, apiBaseUrl, sessionId]);

  // Trigger Gemini activation animation on open
  useEffect(() => {
    if (isOpen) {
      setIsActivating(true);
      const timer = setTimeout(() => {
        setIsActivating(false);
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Focus input field when chatbot is opened
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Log current bot state when it changes
  useEffect(() => {
    if (currentBotState) {
      console.info('🤖 Bot state transitioned to:', currentBotState);
    }
  }, [currentBotState]);

  // ==================== FILE HANDLING ====================
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setApiError('File size must be less than 10MB');
        return;
      }

      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];

      if (!allowedTypes.includes(file.type)) {
        setApiError('Only PDF, JPG, PNG, Word, and Excel files are allowed');
        return;
      }
    }

    setAttachedFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    console.info(
      '📎 Files selected:',
      files.map(f => ({
        name: f.name,
        size: formatFileSize(f.size),
        type: f.type,
      })),
    );
  }, []);

  const removeFile = useCallback((fileName: string) => {
    setAttachedFiles(prev => prev.filter(file => file.name !== fileName));
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // ==================== FILE UPLOAD TO S3 ====================
  const uploadFileToS3 = async (file: File): Promise<string | null> => {
    try {
      console.group('📁 FILE UPLOAD - GET PRESIGNED URL');
      console.info('File Name:', file.name);
      console.info('File Size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      console.info('File Type:', file.type);

      const { data } = await API.post('/bot/upload-url', {
        filename: file.name,
        mimeType: file.type,
      });

      const { uploadUrl, s3Key } = data;
      console.info('📍 S3 Key:', s3Key);
      console.info('🔗 Upload URL received');

      console.group('📤 UPLOADING TO S3');
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: progressEvent => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(percent);
        },
      });

      console.info('✅ File uploaded successfully to S3');
      console.info('🔑 S3 Key for API:', s3Key);
      console.groupEnd();
      console.groupEnd();

      return s3Key;
    } catch (error) {
      console.error('❌ File upload error:', error);
      setApiError(`File upload failed: ${error}`);
      return null;
    }
  };

  // ==================== MESSAGE HANDLING ====================
  const handleQuickAction = useCallback((action: any) => {
    setInputValue(action.query);
    setTimeout(() => handleSendMessage(action.query), 100);
  }, []);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputValue.trim();

    if (!textToSend && attachedFiles.length === 0) return;

    try {
      setIsLoading(true);
      setApiError(null);

      let s3Key: string | undefined;

      if (attachedFiles.length > 0) {
        setShowFileDialog(true);
        for (const file of attachedFiles) {
          const key = await uploadFileToS3(file);
          if (key) {
            s3Key = key;
          }
        }
        setShowFileDialog(false);
      }

      const userMessage: BotMessage = {
        id: uuidv4(),
        role: 'user',
        content: textToSend,
        timestamp: new Date(),
        attachmentPreviews:
          attachedFiles.length > 0
            ? attachedFiles.map(file => ({
                name: file.name,
                size: formatFileSize(file.size),
                type: file.type,
              }))
            : undefined,
      };

      setMessages(prev => [...prev, userMessage]);
      setConversationHistory(prev => [...prev, userMessage]);
      setInputValue('');
      setAttachedFiles([]);

      console.group('🤖 BOT API REQUEST');
      const apiPayload = {
        message: textToSend,
        sessionId: sessionId || uuidv4(),
        orgCode: orgCode,
        ...(s3Key && { s3Key }),
      };

      console.info('📤 Request Body:', apiPayload);
      console.info('⏰ Sent at:', new Date().toISOString());
      if (s3Key) {
        console.info('📎 S3 Key:', s3Key);
      }
      console.groupEnd();

      const { data } = await API.post<ApiResponse>('/speedboxbot/bot/message', apiPayload);

      console.group('🤖 BOT API RESPONSE');
      console.info('📥 Response:', data);
      console.info('🔄 New State:', data.state);
      if (data.uiAction) {
        console.info('🔗 Edit URL:', data.uiAction.editUrl);
      }
      if (data.relatedChips) {
        console.info('🏷️ Related Chips:', data.relatedChips.length);
      }
      console.groupEnd();

      const botMessage: BotMessage = {
        id: uuidv4(),
        role: 'bot',
        content: data.message,
        timestamp: new Date(),
        state: data.state,
        uiAction: data.uiAction,
        relatedChips: data.relatedChips,
      };

      setMessages(prev => [...prev, botMessage]);
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentBotState(data.state);

      if (data.state === 'prefill_ui' && onNavigate && data.uiAction) {
        console.info('🔗 NAVIGATING TO FORM:', data.uiAction.editUrl);
        onNavigate(data.uiAction.editUrl);
      }
    } catch (error: any) {
      console.group('❌ BOT API ERROR');
      const errorMsg =
        error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Something went wrong';
      console.groupEnd();

      setApiError(`Failed to send message: ${errorMsg}`);

      const errorMessage: BotMessage = {
        id: uuidv4(),
        role: 'bot',
        content: `⚠️ Error: ${errorMsg}. Please try again.`,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBotStateAction = (action: 'yes' | 'no' | 'open-form', url?: string) => {
    if (action === 'open-form' && url) {
      console.info('🔗 NAVIGATING:', url);
      if (onNavigate) {
        onNavigate(url);
      } else {
        window.location.href = url;
      }
    } else {
      const message = action === 'yes' ? 'Yes' : action === 'no' ? 'No' : '';
      console.info('✅ User Action:', message);
      handleSendMessage(message);
    }
  };

  const handleRelatedChipClick = (chip: any) => {
    console.info('🏷️ Related chip clicked:', chip.label);
    setInputValue(chip.query);
    setTimeout(() => handleSendMessage(chip.query), 100);
  };

  const getUserInitials = () => {
    if (userName && userName !== 'User') {
      return userName.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return {
    isOpen,
    setIsOpen,
    messages,
    setMessages,
    inputValue,
    setInputValue,
    isLoading,
    setIsLoading,
    attachedFiles,
    setAttachedFiles,
    showSidebar,
    setShowSidebar,
    apiError,
    setApiError,
    uploadProgress,
    setUploadProgress,
    showFileDialog,
    setShowFileDialog,
    isActivating,
    setIsActivating,
    sessionId,
    currentBotState,
    conversationHistory,
    messagesEndRef,
    fileInputRef,
    chatWindowRef,
    inputRef,
    handleFileSelect,
    removeFile,
    handleQuickAction,
    handleSendMessage,
    handleBotStateAction,
    handleRelatedChipClick,
    getUserInitials,
  };
};
